import { useCallback, useEffect, useRef, useState } from 'react';
import { Location } from 'history';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import * as z from 'zod';
import { getAuthCodeFromLocation } from '../../lib/auth';
import {
  getActiveSsoProviderId,
  isIframeEmbedded,
} from '../../lib/embeddedSsoSession';
import {
  clearPendingSsoProvider,
  setPendingSsoProvider,
} from '../../lib/ssoSessionProvider';
import {
  asScalar,
  AUTO_LOGIN_SSO_PROVIDER_ID,
  parse,
  SSO_FLOW,
} from '../../lib/url';
import { authSignIn } from '../../redux/modules/auth/actions';
import { useCreateProviderUrl } from './useCreateProviderUrl';
import { useDomainSettings } from './useDomainSettings';

export type AutoSsoLoginState =
  | { type: 'inactive' }
  | { type: 'loadingSsoProviders' }
  | { type: 'button'; providerName: string; onClick: () => void }
  | { type: 'waitingForSsoConfirmation' };

const ssoFlowSchema = z.enum(['redirect', 'popup']).catch('redirect');

// Iframe-embedded autologin persists sso_flow in localStorage so the popup flow
// survives logout/reload (see SSO_FLOWS.md). URL value takes precedence.
const readAndPersistParam = (
  parsedSearch: Record<string, string | (string | null)[] | null>,
  key: string
): string | undefined => {
  const urlValue = asScalar(parsedSearch[key]);

  if (urlValue) {
    localStorage.setItem(key, urlValue);
    return urlValue;
  }

  return localStorage.getItem(key) ?? undefined;
};

// Standalone (non-iframe) autologin is driven purely by the link: read the param
// from the current URL, else from the referrer that RequireAuth stashed when it
// redirected a deep-linked protected route to the login page. Nothing is
// persisted, so it can't stick across tabs and clash with classic login.
const resolveAutoLoginParam = (
  location: Location<{ referrer?: Location<unknown> }>,
  key: string
): string | undefined => {
  const fromUrl = asScalar(parse(location.search)[key]);

  if (fromUrl) {
    return fromUrl;
  }

  const referrer = location.state?.referrer;

  return referrer
    ? (asScalar(parse(referrer.search)[key]) ?? undefined)
    : undefined;
};

// When iframe-embedded, the provider id is pinned per-tab (sessionStorage) by
// initEmbeddedSsoSession at startup, so concurrent parents sharing this origin
// don't overwrite each other in shared localStorage; preferring the pin also
// keeps the login screen showing only that provider after logout. Otherwise the
// standalone flow resolves it from the link (URL → referrer).
const getAutoLoginProviderId = (
  location: Location<{ referrer?: Location<unknown> }>
): string | undefined => {
  const pinnedProviderId = getActiveSsoProviderId();

  if (pinnedProviderId) {
    return pinnedProviderId;
  }

  return isIframeEmbedded()
    ? undefined
    : resolveAutoLoginParam(location, AUTO_LOGIN_SSO_PROVIDER_ID);
};

export const useAutoSsoLogin = ({
  domain,
}: {
  domain: string;
}): AutoSsoLoginState => {
  const query = useDomainSettings({ domain });
  const dispatch = useDispatch();

  const location = useLocation<{ referrer?: Location<unknown> }>();
  const parsedSearch = parse(location.search);

  // Iframe keeps the persisted sso_flow (popup-flow continuity); standalone reads
  // it from the link (URL → referrer), defaulting to redirect.
  const ssoFlow = ssoFlowSchema.parse(
    isIframeEmbedded()
      ? readAndPersistParam(parsedSearch, SSO_FLOW)
      : resolveAutoLoginParam(location, SSO_FLOW)
  );

  const autoLoginProviderId = getAutoLoginProviderId(location);
  const parsedAutoLoginId = z.coerce.number().safeParse(autoLoginProviderId);

  const foundSsoProvider = query.data?.ssoProviders.find(({ id }) =>
    'data' in parsedAutoLoginId ? id === parsedAutoLoginId.data : false
  );

  const isLoadingProviders =
    parsedAutoLoginId.success && query.status === 'loading' && !query.isFetched;

  const { referrer } = location.state || {};

  const createProviderUrl = useCreateProviderUrl({
    referrer: ssoFlow === 'popup' ? undefined : referrer,
    domain,
    popupRedirect: ssoFlow === 'popup',
  });

  // No autologin intent on this page (no provider in URL/referrer) — drop any
  // stale pending provider so a cancelled SSO redirect can't get promoted by a
  // later login. But NOT while an auth code is being exchanged: after returning
  // from the IdP the redirect_url has been stripped of the provider param, so
  // there's no "intent" here even though the pending provider set before the
  // redirect must survive until signInEpic promotes it.
  useEffect(() => {
    if (!autoLoginProviderId && !getAuthCodeFromLocation(location)) {
      clearPendingSsoProvider();
    }
  }, [autoLoginProviderId, location]);

  // --- Redirect flow: auto-redirect to SSO provider ---
  useEffect(() => {
    if (ssoFlow !== 'redirect' || !foundSsoProvider) {
      return;
    }

    // Record the provider we're signing in with so it can be promoted to the
    // session provider on success (standalone only; iframe uses the pin).
    if (!isIframeEmbedded()) {
      setPendingSsoProvider(String(foundSsoProvider.id));
    }

    window.location.href = createProviderUrl(foundSsoProvider);
  }, [ssoFlow, foundSsoProvider, createProviderUrl]);

  // --- Popup flow: open popup on user click, listen for token ---
  const popupRef = useRef<Window | null>(null);
  const [isWaitingForSso, setIsWaitingForSso] = useState(false);

  const openSsoPopup = useCallback(() => {
    if (ssoFlow !== 'popup' || !foundSsoProvider || popupRef.current) {
      return;
    }

    const ssoUrl = createProviderUrl(foundSsoProvider);
    popupRef.current = window.open(
      `/sso-relay?url=${encodeURIComponent(ssoUrl)}`,
      'sso-popup',
      'width=500,height=600'
    );
    setIsWaitingForSso(true);
  }, [ssoFlow, foundSsoProvider, createProviderUrl]);

  useEffect(() => {
    if (ssoFlow !== 'popup' || !parsedAutoLoginId.success) {
      return undefined;
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin === window.location.origin &&
        event.data?.type === 'sso-token'
      ) {
        dispatch(authSignIn(event.data.token));
        // In iframe-embedded popup flow the SSO callback handed the token off
        // via the legacy `secureToken` key, but it is now stored under the
        // provider-namespaced key. Drop the residual legacy entry so another
        // (non-namespaced) session on this origin can't pick it up.
        if (getActiveSsoProviderId()) {
          localStorage.removeItem('secureToken');
        }
        popupRef.current?.close();
        popupRef.current = null;
        setIsWaitingForSso(false);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [ssoFlow, parsedAutoLoginId.success, dispatch]);

  // --- Derive state ---
  if (isLoadingProviders) {
    return { type: 'loadingSsoProviders' };
  }

  if (isWaitingForSso) {
    return { type: 'waitingForSsoConfirmation' };
  }

  if (ssoFlow === 'redirect' && foundSsoProvider) {
    return { type: 'waitingForSsoConfirmation' };
  }

  if (ssoFlow === 'popup' && foundSsoProvider) {
    return {
      type: 'button',
      providerName: foundSsoProvider.name,
      onClick: openSsoPopup,
    };
  }

  return { type: 'inactive' };
};
